import { useCallback, useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// GET /api/services/mode/{userId} (Section 4.1). The mode string is cached in
// localStorage as `services_mode_${userId}`, exactly like `pc_connected_${userId}`
// (R-MODE 1). With no cached value the hook starts at 'off', so a user whose
// church has the flag off never sees a skeleton or a flicker (rule 15).
//
// `loading` is true only while a cached non-'off' mode is being re-checked (so
// a church whose flag was switched off never flashes a stale Services card);
// `resolved` turns true once the first fetch of this page load has settled.

const cacheKey = (userId) => `services_mode_${userId}`;

function readCachedMode(userId) {
  if (!userId) return null;
  try { return localStorage.getItem(cacheKey(userId)); } catch { return null; }
}

// One in-flight request and the last answer per user, shared by every hook
// instance on the page (dashboard + widget + mini-app) so a page load costs
// one /mode call, never one per component.
const inflight = new Map();
const lastResult = new Map();
const lastFetchedAt = new Map();
// A second component mounting right after the first answer (the dashboard
// widget appears only once mode is known) reuses it instead of refetching.
const FRESH_MS = 10 * 1000;

function fetchMode(userId) {
  if (inflight.has(userId)) return inflight.get(userId);
  const p = (async () => {
    try {
      const res = await fetch(`${API_URL}/api/services/mode/${encodeURIComponent(userId)}`);
      if (!res.ok) return null;
      const data = await res.json();
      lastResult.set(userId, data);
      lastFetchedAt.set(userId, Date.now());
      try { localStorage.setItem(cacheKey(userId), String(data.mode || 'off')); } catch { /* ignore */ }
      return data;
    } catch {
      return null;
    } finally {
      inflight.delete(userId);
    }
  })();
  inflight.set(userId, p);
  return p;
}

// Fix 7 G4: who lands where on a Services notification tap. The last /mode
// answer's is_admin from the shared cache (no network call); while it is
// unknown, the user's own role (owner/admin) decides.
export function servicesAdminKnown(userId) {
  if (!userId || !lastResult.has(userId)) return null;
  return !!lastResult.get(userId).is_admin;
}

export function servicesAdminFor(user) {
  const known = servicesAdminKnown(user?.id);
  return known !== null ? known : (user?.role === 'owner' || user?.role === 'admin');
}

export function useServicesMode(userId) {
  const [cached] = useState(() => readCachedMode(userId));
  const [data, setData] = useState(() => lastResult.get(userId) || null);
  const [resolved, setResolved] = useState(() => lastResult.has(userId));

  const refetch = useCallback(async () => {
    if (!userId) return null;
    const out = await fetchMode(userId);
    if (out) setData(out);
    setResolved(true);
    return out;
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const fresh = lastResult.has(userId) && Date.now() - (lastFetchedAt.get(userId) || 0) < FRESH_MS;
    if (fresh) {
      setData(lastResult.get(userId));
      setResolved(true);
      return;
    }
    refetch();
  }, [userId, refetch]);

  const mode = data?.mode || cached || 'off';
  return {
    mode,
    isAdmin: !!data?.is_admin,
    hasTemplate: !!data?.has_template,
    tabVisible: !!data?.tab_visible,
    horizonWeeks: data?.horizon_weeks || 8,
    churchId: data?.church_id || null,
    loading: !resolved && !!cached && cached !== 'off',
    resolved,
    refetch,
  };
}

export default useServicesMode;
